import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../stores';
import { CoinIcon, PersonFillIcon, TrashIcon } from '../../../model/icons';
import { formatDt } from './ttUtils';
import RejectModal from './RejectModal';

const MEDIA_TABS = [
    { type: 'VIDEO', label: 'Видео' },
    { type: 'PHOTO', label: 'Фото' },
    { type: 'AUDIO', label: 'Звук' }
];

// Карточка сдачи: аватар отправителя (как в челленджах), задание + описание,
// цветной тег команды, медиа по табам видео/фото/звук, вердикт для PENDING.
export default function SubmissionCard({ submission, task, team, submitter, onChanged }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();
    const [rejectOpen, setRejectOpen] = useState(false);
    const [mediaTab, setMediaTab] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    const { data: details, refetch: refetchDetails } = useApiQuery(
        ['teamtask', 'submission', submission.id],
        `/api/team-tasks/admin/submissions/find-by-id?submissionId=${submission.id}`,
        { refetchOnMount: 'always' }
    );
    const media = details?.media || [];

    // Табы только по типам, которые реально пришли; активен выбранный или первый доступный.
    const availableTabs = MEDIA_TABS.filter(t => media.some(m => m.mediaType === t.type));
    const activeType = availableTabs.some(t => t.type === mediaTab) ? mediaTab : availableTabs[0]?.type;
    const shownMedia = media.filter(m => m.mediaType === activeType);

    const pending = submission.status === 'PENDING';
    const teamName = team?.name || `Команда #${submission.teamId}`;
    const teamColor = team?.color || 'var(--navy)';
    const submitterName = submitter
        ? `${submitter.surname || ''} ${submitter.name || ''}`.trim() || submitter.username
        : `userId ${submission.submittedByUserId}`;

    const verify = async (verdict, comment) => {
        show();
        try {
            const params = new URLSearchParams({ submissionId: submission.id, verdict });
            if (comment) params.append('comment', comment);
            await apiClient.post(`/api/team-tasks/admin/submissions/verify?${params.toString()}`);
            enqueueSnackbar(
                verdict === 'APPROVED' ? 'Сдача подтверждена, награда начислена команде' : 'Сдача отклонена, команда уведомлена',
                { variant: 'success' }
            );
            setRejectOpen(false);
            onChanged();
        } catch (e) {
            const status = e.response?.status;
            if (status === 409) {
                enqueueSnackbar('Сдача уже обработана', { variant: 'warning' });
                setRejectOpen(false);
                onChanged();
            } else if (status === 503) {
                enqueueSnackbar('Игра «Сосед» недоступна, начисление не выполнено — повторите позже', { variant: 'error' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Ошибка при вынесении вердикта', { variant: 'error' });
            }
        } finally { hide(); }
    };

    // Модерация контента: удаление одного файла сдачи, статус сдачи и награда не меняются.
    // useCallback — стабильный проп для мемоизированного MediaItem.
    const deleteMedia = React.useCallback((m) => openConfirm(
        <div className="confirm-msg">
            <b>Удалить файл?</b>
            <p>Файл будет удалён без изменения статуса сдачи. Действие необратимо.</p>
        </div>,
        async () => {
            show();
            try {
                await apiClient.delete(`/api/team-tasks/admin/submissions/media?mediaId=${m.id}`);
                enqueueSnackbar('Файл удалён', { variant: 'success' });
                refetchDetails();
            } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при удалении файла', { variant: 'error' }); }
            finally { hide(); }
        },
        { confirmText: 'Удалить' }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [openConfirm, show, hide, enqueueSnackbar]);

    const confirmApprove = () => openConfirm(
        <div className="confirm-msg">
            <b>Подтвердить выполнение?</b>
            <p>
                Команда «{teamName}» получит награду: <b>{task?.rewardBalance ?? '?'}</b> баллов каждому игроку
                и <b>{task?.coinBank ?? '?'}</b> Coin Limited на команду (делится поровну). Действие необратимо.
            </p>
        </div>,
        () => verify('APPROVED'),
        { confirmText: 'Подтвердить' }
    );

    return (
        <div className="tt-sub">
            <div className="tt-sub-head">
                <span className="tt-ava" style={{ background: teamColor }}>
                    {submitter?.avatarUrl ? <img src={submitter.avatarUrl} alt="" /> : <PersonFillIcon />}
                </span>
                <div className="tt-sub-who">
                    <div className="tt-sub-title">{task?.title || `Задание #${submission.taskId}`}</div>
                    <div className="tt-sub-meta">
                        <span>{submitterName}</span>
                        <span className="dot">·</span>
                        <span>{formatDt(submission.createdAt)}</span>
                        {submission.status === 'APPROVED' && submission.paidAt && (
                            <>
                                <span className="dot">·</span>
                                <span>начислено {formatDt(submission.paidAt)}</span>
                            </>
                        )}
                    </div>
                </div>
                <span className="tt-team-tag" style={{ background: teamColor }}>{teamName}</span>
            </div>

            {task?.description && <div className="tt-sub-desc">{task.description}</div>}

            {task && (
                <div className="tt-sub-reward">
                    <span className="tt-tag pts" title="Баллы каждому игроку команды">Каждому участнику <b>{task.rewardBalance}</b> <CoinIcon /></span>
                    <span className="tt-tag coin" title="Coin Limited на команду (делится поровну)">Coin Limited <b>{task.coinBank}</b> <CoinIcon /></span>
                </div>
            )}

            {media.length > 0 ? (
                <>
                    {availableTabs.length > 1 && (
                        <div className="tt-mtabs">
                            {availableTabs.map(t => (
                                <button
                                    type="button"
                                    key={t.type}
                                    className={`tt-mtab${activeType === t.type ? ' on' : ''}`}
                                    onClick={() => setMediaTab(t.type)}
                                >
                                    {t.label}
                                    <span className="n">{media.filter(m => m.mediaType === t.type).length}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="tt-media">
                        {shownMedia.map(m => <MediaItem key={m.id} media={m} onOpen={setLightbox} onDelete={deleteMedia} />)}
                    </div>
                </>
            ) : <div className="bt-empty">Медиа недоступны</div>}

            {submission.status === 'REJECTED' && submission.reviewComment && (
                <div className="tt-sub-comment">Комментарий: {submission.reviewComment}</div>
            )}

            {pending && (
                <div className="tt-sub-acts">
                    <button type="button" className="btn btn-ok btn-sm" onClick={confirmApprove}>Подтвердить</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => setRejectOpen(true)}>Отклонить</button>
                </div>
            )}

            {rejectOpen && (
                <RejectModal
                    title={`${task?.title || 'Задание'} · ${teamName}`}
                    onReject={(comment) => verify('REJECTED', comment)}
                    onClose={() => setRejectOpen(false)}
                />
            )}

            {lightbox && (
                <div className="scrim show tt-lightbox" onMouseDown={() => setLightbox(null)}>
                    <img src={lightbox} alt="" />
                </div>
            )}
        </div>
    );
}

SubmissionCard.propTypes = {
    submission: PropTypes.object.isRequired,
    task: PropTypes.object,
    team: PropTypes.shape({ name: PropTypes.string, color: PropTypes.string }),
    submitter: PropTypes.object,
    onChanged: PropTypes.func.isRequired
};

SubmissionCard.defaultProps = { task: null, team: null, submitter: null };

// React.memo: открытие лайтбокса/таба/модалки отклонения не перерисовывает видео и аудио.
const MediaItem = React.memo(function MediaItem({ media, onOpen, onDelete }) {
    const delBtn = (
        <button type="button" className="media-del" title="Удалить файл" onClick={() => onDelete(media)}>
            <TrashIcon />
        </button>
    );
    if (media.mediaType === 'VIDEO') {
        return (
            <div className="media-delwrap">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video className="tt-video" src={media.url} controls preload="metadata" />
                {delBtn}
            </div>
        );
    }
    if (media.mediaType === 'AUDIO') {
        return (
            <div className="media-delwrap aud">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio className="tt-audio" src={media.url} controls preload="metadata" />
                {delBtn}
            </div>
        );
    }
    return (
        <div className="media-delwrap">
            <button type="button" className="tt-photo" title="Открыть фото" onClick={() => onOpen(media.url)}>
                <img src={media.url} alt="" />
            </button>
            {delBtn}
        </div>
    );
});

MediaItem.propTypes = {
    media: PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string.isRequired,
        mediaType: PropTypes.string
    }).isRequired,
    onOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};
